import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppInformationService, AuthService, GenesisUnitOfWork, SessionInformationService } from 'app/core/core.module';
import { InvoicePaymentMethod, OrderPaymentMethod, Payment } from 'app/core/model/entity-model';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { confirm } from 'devextreme/ui/dialog';
import { BsModalService } from 'ngx-bootstrap/modal';

@Component({
    selector: 'app-payment-search',
    templateUrl: './payment-search.component.html',
    styleUrls: ['./payment-search.component.scss'],
    standalone: false
})
export class PaymentSearchComponent implements OnInit {
    payments: Payment[];
    paymentMethods: InvoicePaymentMethod[];
    filter: { showAll: boolean };

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;
    onOptionChanged = SharedModule.onOptionChanged;

    constructor(
        private uow: GenesisUnitOfWork,
        private router: Router,
        public auth: AuthService,
        private sessionInfo: SessionInformationService,
        private appInfo: AppInformationService,
        private modalService: BsModalService
    ) {}

    async ngOnInit() {
        this.paymentMethods = await this.uow.invoicePaymentMethods.whereActive('displayOrder') as InvoicePaymentMethod[];
        this.filter = this.sessionInfo.getItem('payment-search-filter') ?? { showAll: false };
        await this.getData();
    }

    async getData() {
        this.appInfo.pleaseWaitShow();
        this.payments = await this.uow.getPayments(!this.filter.showAll, 'paymentLines.invoice, paymentLines.packingSlip, dealer, paymentLines.orderLineDetail.orderLine.order') as Payment[];
        this.appInfo.pleaseWaitHide();
    }

    async toggleShowAll() {
        this.filter.showAll = !this.filter.showAll;
        await this.getData();
        this.sessionInfo.setItem('payment-search-filter', this.filter);
    }

    async rowPrepare(e) {
        if (e.rowType === 'data') {
            if (e.data.quickBooksId) {
                e.rowElement.className = e.rowElement.className + ' payment-exported';
            }
            if (e.data.readyToExport && !e.data.quickBooksId) {
                e.rowElement.className = e.rowElement.className + ' payment-ready-to-export';
            }
        }
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            }
        );
    }

    async open(payment: Payment) {
        if (!payment) return;
        this.router.navigate([`/payments/${payment.id}`])
    }

    async rowDoubleClick(e) {
        if (!e.data) return;
        await this.open(e.data);
    }

    async markReadyForExport(payments: Payment[]) {
        if (!payments) return;

        const success = await confirm(
            `Are you sure you want to mark the selected payment${payments.length === 1 ? '' : 's'} as Ready To Export for QuickBooks?`,
            this.appInfo.settings.company
        );
        if (!success) return;

        const date = new Date();
        payments.forEach((payment) => {
            if (!payment.readyToExport) payment.readyToExport = date;
        });
        await this.uow.commit();
        await this.getData();
        this.dataGrid.instance.clearSelection();
    }

    async payWithoutQuickBooks(payment: Payment) {
        if (!payment || payment.quickBooksId || payment.quickBooksLastExport) return;

        const success = await confirm('Are you sure you want to mark this payment as paid without QuickBooks?', this.appInfo.settings.company);
        if (!success) return;

        payment.quickBooksLastExport = new Date();
        payment.quickBooksId = 'Marked paid w/o QB';
        await this.uow.commit([payment]);
        this.dataGrid.instance.clearSelection();
      }
}
